import type {
    BetaDashboardTask,
    BetaPage,
    BetaPageAssessment,
    BetaReadinessStatus,
} from '@/types/beta-dashboard'

const TEXT_FRAME_TYPES = new Set(['text', 'title'])
const MEDIA_FRAME_TYPES = new Set([
    'beforeafterimage',
    'before-after-image',
    'before_after_image',
    'gallery',
    'image',
    'video',
])
const MEDIA_VALUE_KEYS = new Set([
    'afterPicture',
    'background_image',
    'backgroundImage',
    'beforePicture',
    'content',
    'coverImage',
    'embed',
    'image',
    'mainImage',
    'src',
    'thumbnail',
    'url',
    'uri',
])

const isRecord = (value: unknown): value is Record<string, unknown> => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
)

const normalizeText = (value: string) => value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const hasNonEmptyValue = (value: unknown): boolean => {
    if (typeof value === 'string') return normalizeText(value).length > 0
    if (typeof value === 'number') return true
    if (Array.isArray(value)) return value.some(hasNonEmptyValue)
    if (!isRecord(value)) return false

    return Object.values(value).some(hasNonEmptyValue)
}

const normalizeFrameType = (value: unknown) => String(value ?? '').trim().toLowerCase()

const hasMediaReference = (value: unknown, allowDirectString = false): boolean => {
    if (typeof value === 'string') {
        return allowDirectString && normalizeText(value).length > 0
    }

    if (Array.isArray(value)) {
        return value.some((item) => hasMediaReference(item, allowDirectString))
    }

    if (!isRecord(value)) return false

    if ([...MEDIA_VALUE_KEYS].some((key) => (
        Object.prototype.hasOwnProperty.call(value, key)
        && hasMediaReference(value[key], true)
    ))) {
        return true
    }

    return Object.values(value).some((item) => hasMediaReference(item))
}

export const hasPersistedMedia = (value: unknown, insideMediaFrame = false): boolean => {
    if (Array.isArray(value)) {
        return value.some((item) => hasPersistedMedia(item, insideMediaFrame))
    }

    if (!isRecord(value)) return false

    const frameType = normalizeFrameType(value.frameType)
    const isMediaFrame = insideMediaFrame || MEDIA_FRAME_TYPES.has(frameType)

    if (isMediaFrame) {
        if (hasMediaReference(value.frameContent, true)) return true
    }

    if ([...MEDIA_VALUE_KEYS].some((key) => (
        Object.prototype.hasOwnProperty.call(value, key)
        && hasMediaReference(value[key], true)
    ))) {
        return true
    }

    return Object.values(value).some((item) => hasPersistedMedia(item, isMediaFrame))
}

export const hasMeaningfulPageText = (value: unknown): boolean => {
    if (Array.isArray(value)) {
        return value.some(hasMeaningfulPageText)
    }

    if (!isRecord(value)) return false

    const frameType = normalizeFrameType(value.frameType)
    if (TEXT_FRAME_TYPES.has(frameType) && hasNonEmptyValue(value.frameContent)) {
        return true
    }

    return Object.values(value).some(hasMeaningfulPageText)
}

export const collectComponentPrefixes = (value: unknown, prefixes = new Set<string>()) => {
    if (Array.isArray(value)) {
        value.forEach((item) => collectComponentPrefixes(item, prefixes))
        return prefixes
    }

    if (!isRecord(value)) return prefixes

    if (typeof value.component === 'string' && value.component.trim()) {
        prefixes.add(value.component.split('-')[0].toLowerCase())
    }

    Object.values(value).forEach((item) => collectComponentPrefixes(item, prefixes))
    return prefixes
}

export const assessBetaPage = (
    page: BetaPage,
    document: unknown,
): BetaPageAssessment => {
    const componentPrefixes = [...collectComponentPrefixes(document)]
    const isHome = Boolean(page.isHome) || componentPrefixes.includes('home')
    const isList = page.listPages == 1 || componentPrefixes.includes('listblog')
    const isAbout = componentPrefixes.includes('about')
    const isContact = componentPrefixes.includes('contact')

    const kind = isHome
        ? 'home'
        : isList
            ? 'list'
            : isAbout
                ? 'about'
                : isContact
                    ? 'contact'
                    : 'content'

    return {
        pageId: page.id,
        kind,
        componentPrefixes,
        hasMedia: hasPersistedMedia(document),
        hasText: hasMeaningfulPageText(document),
    }
}

type ReadinessInput = {
    identityReady: boolean
    contactReady: boolean
    contentLoaded: boolean
    contentHasErrors: boolean
    homeMediaStatus: BetaReadinessStatus
    homeCopyStatus: BetaReadinessStatus
    aboutStatus: BetaReadinessStatus | 'not-applicable'
    portfolioStatus: BetaReadinessStatus | 'not-applicable'
    reviewed: boolean
    customDomainReady: boolean
    domainDismissed: boolean
}

const statusToTaskState = (
    status: BetaReadinessStatus,
): BetaDashboardTask['state'] => {
    if (status === 'complete') return 'complete'
    if (status === 'pending') return 'pending'
    if (status === 'unsupported') return 'unsupported'
    return 'unknown'
}

export const buildBetaReadinessTasks = (input: ReadinessInput): BetaDashboardTask[] => {
    const homeMediaAction: BetaDashboardTask['action'] = input.homeMediaStatus === 'unsupported'
        ? 'classic'
        : input.homeMediaStatus === 'unknown' ? 'reload' : 'home'
    const homeCopyAction: BetaDashboardTask['action'] = input.homeCopyStatus === 'unsupported'
        ? 'classic'
        : input.homeCopyStatus === 'unknown' ? 'reload' : 'home'
    const homeMediaCta = input.homeMediaStatus === 'unsupported'
        ? 'Abrir painel clássico'
        : input.homeMediaStatus === 'unknown' ? 'Tentar novamente' : 'Editar Home'
    const homeCopyCta = input.homeCopyStatus === 'unsupported'
        ? 'Abrir painel clássico'
        : input.homeCopyStatus === 'unknown' ? 'Tentar novamente' : 'Editar Home'
    const tasks: BetaDashboardTask[] = [
        {
            id: 'identity.ready',
            action: 'identity',
            title: 'Confirme sua identidade',
            description: 'Revise o nome e o logo que aparecem no seu site.',
            cta: 'Editar identidade',
            state: input.identityReady ? 'complete' : 'pending',
            weight: 1,
            mandatory: true,
        },
    ]

    if (!input.contentLoaded) {
        tasks.push({
            id: 'content.verify',
            action: 'reload',
            title: 'Verifique o conteúdo criado',
            description: 'Ainda não foi possível confirmar a estrutura gerada pelo seu layout.',
            cta: 'Tentar novamente',
            state: 'unknown',
            weight: 1,
            mandatory: true,
        })
    } else {
        tasks.push(
            {
                id: 'home.media',
                action: homeMediaAction,
                title: 'Escolha sua melhor imagem',
                description: input.homeMediaStatus === 'unsupported'
                    ? 'A Home deste layout precisa ser revisada no painel clássico.'
                    : 'Uma foto forte na Home causa uma boa primeira impressão.',
                cta: homeMediaCta,
                state: statusToTaskState(input.homeMediaStatus),
                weight: 1,
                mandatory: true,
            },
            {
                id: 'home.copy',
                action: homeCopyAction,
                title: 'Conte o que você faz',
                description: input.homeCopyStatus === 'unsupported'
                    ? 'O texto principal deste layout precisa ser revisado no painel clássico.'
                    : 'Use uma frase curta para explicar seu trabalho logo na primeira tela.',
                cta: homeCopyCta,
                state: statusToTaskState(input.homeCopyStatus),
                weight: 1,
                mandatory: true,
            },
        )

        if (input.aboutStatus !== 'not-applicable') {
            tasks.push({
                id: 'about.content',
                action: input.aboutStatus === 'unsupported' ? 'classic' : 'about',
                title: 'Apresente você ou seu negócio',
                description: input.aboutStatus === 'unsupported'
                    ? 'A página Sobre deste layout precisa ser revisada no painel clássico.'
                    : 'Complete a página Sobre com uma imagem e uma descrição clara.',
                cta: input.aboutStatus === 'unsupported' ? 'Abrir painel clássico' : 'Editar Sobre',
                state: statusToTaskState(input.aboutStatus),
                weight: 1,
                mandatory: true,
            })
        }

        if (input.portfolioStatus !== 'not-applicable') {
            tasks.push({
                id: 'portfolio.first_item',
                action: input.portfolioStatus === 'unknown' ? 'reload' : 'content',
                title: 'Publique seu primeiro trabalho',
                description: 'Adicione uma galeria ou publicação a uma página de conteúdo.',
                cta: input.portfolioStatus === 'unknown' ? 'Tentar novamente' : 'Abrir conteúdo',
                state: statusToTaskState(input.portfolioStatus),
                weight: 1,
                mandatory: true,
            })
        }

        if (input.contentHasErrors) {
            tasks.push({
                id: 'content.partial',
                action: 'reload',
                title: 'Confirme as páginas do seu site',
                description: 'Parte do conteúdo não pôde ser verificada nesta tentativa.',
                cta: 'Tentar novamente',
                state: 'unknown',
                weight: 1,
                mandatory: true,
            })
        }
    }

    tasks.push(
        {
            id: 'contact.channel',
            action: 'contact',
            title: 'Deixe um canal de contato',
            description: 'Informe ao menos um e-mail, telefone ou WhatsApp para novos clientes.',
            cta: 'Editar contato',
            state: input.contactReady ? 'complete' : 'pending',
            weight: 1,
            mandatory: true,
        },
        {
            id: 'site.reviewed',
            action: 'preview',
            title: 'Revise seu site',
            description: 'Abra o site publicado e confira como ele aparece para seus visitantes.',
            cta: input.reviewed ? 'Revisado' : 'Ver e confirmar',
            state: input.reviewed ? 'complete' : 'pending',
            weight: 0,
            mandatory: false,
        },
        {
            id: 'domain.custom',
            action: 'domain',
            title: 'Conecte seu domínio',
            description: 'Use um endereço próprio para fortalecer sua marca.',
            cta: input.customDomainReady ? 'Configurado' : 'Configurar domínio',
            state: input.customDomainReady
                ? 'complete'
                : input.domainDismissed ? 'dismissed' : 'pending',
            weight: 0,
            mandatory: false,
        },
    )

    return tasks
}
